import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { openSlider } from 'utils/sportsbook/api-post-message-sender';
import { BetBuilderModalActions } from 'components/Generic/__partials/BetBuilderModalGeneric/actions';
import { addQuery, saveCurrentLocation } from 'components/QueryRoute';
import { TBetBuilderModalProps } from 'newcomponents/Shared/sportsbook/BetBuilderModal/types';
import { usePrevious } from 'hooks/usePrevious';
import { setAfterSignInActions } from 'store/actions';
import { getAfterSignInActions } from 'store/selectors/app-data';
import { getIsLoggedIn, getUser } from 'store/selectors/user-data';

type TUseOpenBetBuilder = (
  params: Pick<TBetBuilderModalProps, 'fixtureId' | 'sportId' | 'gameTitle'> & {
    afterSignInActonName: string;
  },
  openCallback?: null | (() => void)
) => TUseOpenBetBuilderReturnType;

type TUseOpenBetBuilderReturnType = {
  betBuilderModalVisible: boolean;
  openBetBuilder: Function;
  toggleBetBuilderModal: (value: boolean) => void;
};

export const useOpenBetBuilder: TUseOpenBetBuilder = (params, openCallback) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const afterSignInActions = useSelector(getAfterSignInActions);
  const [betBuilderModalVisible, _toggleBetBuilderModal] =
    useState<boolean>(false);

  const [openAfterRefresh, setOpenAfterRefresh] = useState(false);
  const isPrevOpened = usePrevious(betBuilderModalVisible);

  const toggleBetBuilderModal = (open: boolean) => {
    if (openCallback) {
      if (open) {
        openCallback();
      }
    } else {
      _toggleBetBuilderModal(open);
    }
  };

  useEffect(() => {
    if (
      afterSignInActions.openBetBuilderAction === params.afterSignInActonName &&
      isLoggedIn &&
      !window.mustRefreshForSessionVisibility &&
      window.refreshWhenLoggedIn &&
      !openAfterRefresh
    ) {
      toggleBetBuilderModal(true);
      const afterSignInActionsCopy = Object.assign({}, afterSignInActions);
      delete afterSignInActionsCopy.openBetBuilderAction;
      dispatch(setAfterSignInActions(afterSignInActionsCopy));
    }
  }, [isLoggedIn, openAfterRefresh, afterSignInActions.openBetBuilderAction]);

  useEffect(() => {
    if (
      afterSignInActions.openBetBuilderAction === params.afterSignInActonName &&
      isLoggedIn &&
      !window.refreshWhenLoggedIn &&
      openAfterRefresh
    ) {
      setOpenAfterRefresh(false);
      toggleBetBuilderModal(true);
      const afterSignInActionsCopy = Object.assign({}, afterSignInActions);
      delete afterSignInActionsCopy.openBetBuilderAction;
      dispatch(setAfterSignInActions(afterSignInActionsCopy));
    }
  }, [isLoggedIn, openAfterRefresh, afterSignInActions.openBetBuilderAction]);

  const openBetBuilder = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation();

      if (!user.pending) {
        if (isLoggedIn) {
          toggleBetBuilderModal(true);
        } else {
          if (SpringConfigs.IFRAME_SPORTSBOOK) {
            openSlider('login');
          } else {
            saveCurrentLocation();

            history.push(addQuery({ accounts: '*', login: '*' }));

            setOpenAfterRefresh(true);
            dispatch(
              setAfterSignInActions({
                ...afterSignInActions,
                openBetBuilderAction: params.afterSignInActonName
              })
            );
          }
        }
      }
    },
    [
      history,
      isLoggedIn,
      toggleBetBuilderModal,
      user.pending,
      afterSignInActions
    ]
  );

  const betBuilderModalParams = useMemo(() => {
    return {
      ...params,
      toggleBetBuilderModal,
      betBuilderModalVisible
    };
  }, [betBuilderModalVisible, JSON.stringify(params)]);

  useLayoutEffect(() => {
    if (betBuilderModalParams.betBuilderModalVisible) {
      BetBuilderModalActions.updateProps(betBuilderModalParams);
    }
  }, [betBuilderModalParams]);

  useEffect(() => {
    if (!betBuilderModalVisible && isPrevOpened) {
      BetBuilderModalActions.close();
    } else if (!isPrevOpened && betBuilderModalVisible) {
      BetBuilderModalActions.open(betBuilderModalParams);
    }
  }, [betBuilderModalVisible, isPrevOpened]);

  return {
    betBuilderModalVisible,
    openBetBuilder,
    toggleBetBuilderModal
  };
};
